"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  updateBillingSettingsAction,
  type BillingSettingsView,
} from "@/server-actions/billing-settings"
import { billingSettingsQueryKey } from "@/hooks/queries/billing-settings"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import { useTranslations } from "next-intl"


export function useUpdateBillingSettingsViewMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsBillingSettings")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      dailySpendingLimitMicrousd: number | null
      monthlySpendingLimitMicrousd: number | null
      lowBalanceThresholdMicrousd: number | null
      lowBalanceEmailEnabled: boolean
      spendingCapEmailEnabled: boolean
    }): Promise<BillingSettingsView> => {
      const result = await withQueryHandling(updateBillingSettingsAction(input), {
        loading: tUi("savingBalanceAlerts"),
        success: tUi("balanceAlertsSaved"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(billingSettingsQueryKey, data)
      // The banner's "low" flag is computed from the same threshold — refetch it
      // so the UI cannot show a low-balance warning at a number the tenant just
      // changed away from.
      await queryClient.invalidateQueries({ queryKey: ["billing"] })
    },
  })
}
